export class TooltipManager {
    #tooltipCache = new Map(); // Cache tooltips by hex
    #currentTooltip = null;
    #baseUrl = `${window.location.protocol}//${window.location.host}`;
    #pendingRequests = new Map(); // Track in-progress requests
    #loading = false; // Global loading flag
    
    initialize() {
        this.#setupEventListeners();
        this.#createTooltipElement();
    }
    
    #createTooltipElement() {
        // Create tooltip element once at initialization
        if (!document.getElementById('itemTooltipRender')) {
            const tooltip = document.createElement('div');
            tooltip.id = 'itemTooltipRender';
            tooltip.className = 'absolute bg-black/80 text-sm rounded-md p-3 text-center z-30 hidden';
            document.body.appendChild(tooltip);
        }
    }

    #setupEventListeners() {
        // Use event delegation with debounce for mouseover events
        let hoverDebounceTimer = null;
        
        document.addEventListener('mouseover', (e) => {
            const itemElement = e.target.closest('.itemTooltipView');
            if (itemElement) {
                clearTimeout(hoverDebounceTimer);
                hoverDebounceTimer = setTimeout(() => {
                    this.#handleItemHover(itemElement, e);
                }, 10); // Short debounce to prevent duplicate events
            }
        });

        document.addEventListener('mouseout', (e) => {
            const itemElement = e.target.closest('.itemTooltipView');
            if (itemElement && !itemElement.contains(e.relatedTarget)) {
                clearTimeout(hoverDebounceTimer);
                this.hideTooltip();
            }
        });
    }

    async #handleItemHover(element, event) {
        if (!element) return;

        const hex = element.dataset.hex;
        if (!hex) return;
        
        // Store current element being hovered
        this.#currentTooltip = element;
        
        // Check cache first
        if (this.#tooltipCache.has(hex)) {
            this.#showTooltip(this.#tooltipCache.get(hex), element);
            return;
        }
        
        // Prevent duplicate fetches - if we're already loading this item, just wait
        if (this.#pendingRequests.has(hex)) {
            console.log('Already fetching item data for:', hex);
            this.#showTooltip('<div class="loading">Loading...</div>', element);
            
            // Wait for the pending request to complete
            try {
                const data = await this.#pendingRequests.get(hex);
                if (this.#currentTooltip === element) {
                    this.#showTooltip(this.#formatTooltipContent(data), element);
                }
            } catch (error) {
                console.error('Error with pending request:', error);
            }
            return;
        }
        
        // Set loading state and show loading placeholder
        this.#loading = true;
        this.#showTooltip('<div class="loading">Loading...</div>', element);
        
        try {
            console.log('Fetching item data for:', hex);
            
            // Create a promise for this request and store it BEFORE making the request
            const requestPromise = new Promise(async (resolve, reject) => {
                try {
                    const response = await fetch(`${this.#baseUrl}/api/item/${hex}`);
                    if (!response.ok) throw new Error('Network response was not ok');
                    const data = await response.json();
                    resolve(data);
                } catch (error) {
                    reject(error);
                }
            });
            
            // Store the promise immediately
            this.#pendingRequests.set(hex, requestPromise);
            
            // Wait for the request to complete
            const data = await requestPromise;
            
            // Cache the result
            this.#tooltipCache.set(hex, data);
            
            // Update tooltip if still hovering this item
            if (this.#currentTooltip === element) {
                this.#showTooltip(this.#formatTooltipContent(data), element);
            }
            
            // Reset states
            this.#loading = false;
            this.#pendingRequests.delete(hex);
            
        } catch (error) {
            console.error('Error loading tooltip:', error);
            this.#loading = false;
            this.#pendingRequests.delete(hex);
            this.#showTooltip('<div class="error">Could not load item data</div>', element);
        }
    }

    #formatTooltipContent(data) {
        // Format the JSON data returned by API into HTML
        if (typeof data === 'string') {
            return data; // Already formatted as HTML string
        }
        
        try {
            // If data is an object, format it to HTML
            let html = '';
            
            if (data.name) {
                html += `<div class="item-name ${data.rarity || ''}">${data.name}</div>`;
            }
            
            if (data.type) {
                html += `<div class="item-type">${data.type}</div>`;
            }
            
            if (data.stats && Array.isArray(data.stats)) {
                html += '<div class="item-stats">';
                data.stats.forEach(stat => {
                    html += `<div class="stat">${stat}</div>`;
                });
                html += '</div>';
            }
            
            if (data.description) {
                html += `<div class="item-description">${data.description}</div>`;
            }
            
            return html || JSON.stringify(data);
        } catch (error) {
            console.error('Error formatting tooltip data:', error);
            return '<div class="error">Error formatting item data</div>';
        }
    }

    #showTooltip(content, element) {
        // Garantir que o elemento tooltip existe
        this.#createTooltipElement();
        
        const tooltip = document.getElementById('itemTooltipRender');
        if (!tooltip) return;
        
        // Atualizar conteúdo e mostrar
        tooltip.innerHTML = content;
        tooltip.classList.remove('hidden');
        
        // Posicionar relativamente ao elemento que acionou
        this.#positionTooltip(element);
    }

    #positionTooltip(element) {
        if (!element) return;
        
        const tooltip = document.getElementById('itemTooltipRender');
        if (!tooltip || tooltip.classList.contains('hidden')) return;

        // Obter as dimensões e posição do elemento que disparou
        const elementRect = element.getBoundingClientRect();
        
        // Obter as dimensões do tooltip
        const tooltipRect = tooltip.getBoundingClientRect();
        
        // Considerar rolagem da página
        const scrollX = window.scrollX || window.pageXOffset;
        const scrollY = window.scrollY || window.pageYOffset;
        
        // Determinamos a posição ideal para o tooltip
        // Por padrão, posicionamos no centro abaixo do elemento
        let x = scrollX + elementRect.left + (elementRect.width / 2) - (tooltipRect.width / 2);
        let y = scrollY + elementRect.bottom + 10; // 10px de espaço abaixo
        
        // Verificar se o tooltip ficaria fora da tela e ajustar se necessário
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Ajuste para não sair da tela à esquerda
        if (x < scrollX) {
            x = scrollX + 5; // 5px de margem
        }
        
        // Ajuste para não sair da tela à direita
        if (x + tooltipRect.width > scrollX + viewportWidth) {
            x = scrollX + viewportWidth - tooltipRect.width - 5;
        }
        
        // Se o tooltip ficaria fora da parte inferior da tela, 
        // colocamos acima do elemento
        if (y + tooltipRect.height > scrollY + viewportHeight) {
            y = scrollY + elementRect.top - tooltipRect.height - 10;
        }
        
        // Aplicar a posição
        tooltip.style.left = `${x}px`;
        tooltip.style.top = `${y}px`;
    }

    hideTooltip() {
        const tooltip = document.getElementById('itemTooltipRender');
        if (tooltip) {
            tooltip.classList.add('hidden');
        }
        this.#currentTooltip = null;
    }

    // Method to clear cache if needed
    clearCache() {
        this.#tooltipCache.clear();
        this.#pendingRequests.clear();
    }
}